/**
 * Escala de pressão compartilhada pelo heatmap EPANET e pelos marcadores
 * de pressão no mapa de equipamentos. Valores na mesma unidade do heatmap.
 */
export const STOPS_PRESSAO = [
  [0, 'rgba(26, 35, 126, 1)'],
  [5, 'rgba(51, 136, 255, 1)'],
  [10, 'rgba(100, 221, 23, 1)'],
  [15, 'rgba(255, 255, 0, 1)'],
  [20, 'rgba(255, 160, 0, 1)'],
  [30, 'rgba(255, 23, 68, 1)'],
]

export const PASSO_PRESSAO = 2
export const PRESSAO_MAX = 30

/** Nível 0–100: mesma paleta da pressão, invertida (vazio = stop 30). */
export const STOPS_NIVEL = [
  [0, STOPS_PRESSAO[5][1]],
  [20, STOPS_PRESSAO[4][1]],
  [40, STOPS_PRESSAO[3][1]],
  [60, STOPS_PRESSAO[2][1]],
  [80, STOPS_PRESSAO[1][1]],
  [100, STOPS_PRESSAO[0][1]],
]

function parseRgb(cor) {
  const match = String(cor).match(/(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/)
  if (!match) {
    return [0, 0, 0]
  }

  return [Number(match[1]), Number(match[2]), Number(match[3])]
}

function interpolarStops(stops, valor, minimo, maximo) {
  const v = Math.min(maximo, Math.max(minimo, valor))
  let i = 0
  while (i < stops.length - 1 && stops[i + 1][0] < v) {
    i += 1
  }

  const [x0, c0] = stops[i]
  const [x1, c1] = stops[Math.min(i + 1, stops.length - 1)]
  if (x1 === x0) {
    return c0
  }

  const t = (v - x0) / (x1 - x0)
  const a = parseRgb(c0)
  const b = parseRgb(c1)

  return `rgba(${Math.round(a[0] + (b[0] - a[0]) * t)}, ${Math.round(
    a[1] + (b[1] - a[1]) * t
  )}, ${Math.round(a[2] + (b[2] - a[2]) * t)}, 1)`
}

export function corPressao(valor) {
  return interpolarStops(STOPS_PRESSAO, valor, 0, PRESSAO_MAX)
}

export function corAguaNivel(nivel) {
  return interpolarStops(STOPS_NIVEL, nivel, 0, 100)
}

export function faixaPressao(valor) {
  const limitado = Math.min(PRESSAO_MAX, Math.max(0, valor))
  return Math.round(limitado / PASSO_PRESSAO) * PASSO_PRESSAO
}

export function faixasPressao() {
  const faixas = []
  for (let valor = 0; valor <= PRESSAO_MAX; valor += PASSO_PRESSAO) {
    faixas.push(valor)
  }
  return faixas
}

export const CHAVE_JUNCTIONS = 'junctions'
export const PROP_COTA = 'Cota'

export function parseCota(valor) {
  if (valor == null || valor === '') {
    return null
  }

  const n = Number(String(valor).replace(',', '.'))
  return Number.isFinite(n) ? n : null
}

export function faixaCotaNasCamadas(camadas) {
  let min = Infinity
  let max = -Infinity
  ;(camadas || []).forEach((camada) => {
    if (
      camada.visivel === false ||
      camada.chave !== CHAVE_JUNCTIONS ||
      !camada.geojson
    ) {
      return
    }

    ;(camada.geojson.features || []).forEach((feature) => {
      const n = parseCota(feature.properties && feature.properties[PROP_COTA])
      if (n == null) {
        return
      }
      if (n < min) min = n
      if (n > max) max = n
    })
  })

  if (min === Infinity) {
    return null
  }

  return { min, max }
}

function listaCoresCota(cores) {
  if (Array.isArray(cores) && cores.length >= 2) {
    return cores
  }
  return STOPS_PRESSAO.map((stop) => stop[1])
}

export function stopsCota(min, max, cores) {
  const lista = listaCoresCota(cores)
  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return []
  }
  if (min === max) {
    return [[min, lista[Math.floor(lista.length / 2)]]]
  }

  return lista.map((cor, i) => {
    const t = i / (lista.length - 1)
    return [min + (max - min) * t, cor]
  })
}

export function expressaoCorCota(min, max, fallback = '#78909c', cores) {
  const stops = stopsCota(min, max, cores)
  if (stops.length === 0) {
    return fallback
  }

  const temCota = [
    'all',
    ['has', PROP_COTA],
    ['!=', ['to-string', ['get', PROP_COTA]], ''],
  ]

  if (stops.length === 1) {
    return ['case', temCota, stops[0][1], fallback]
  }

  const interpolate = [
    'interpolate',
    ['linear'],
    ['to-number', ['get', PROP_COTA]],
  ]
  stops.forEach(([valor, cor]) => {
    interpolate.push(valor, cor)
  })

  return ['case', temCota, interpolate, fallback]
}
